import { ReactNode } from "react";
import { Link } from "wouter";
import { Search } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Input } from "@/components/ui/input";

/**
 * The shared shell for every collection screen — Documents, Notes, Quotes,
 * Scripture.
 *
 * These four pages had drifted into four different designs: three rendered a
 * `<Table>` with a column per field, one rendered rows; headings ranged over
 * three sizes and weights; two centred their container and two did not. They
 * are the same kind of screen and now share the same parts, so a change to the
 * pattern lands everywhere at once.
 */

interface CollectionPageProps {
  title: string;
  /** Shown under the title as "12 notes". Omit while loading. */
  count?: number;
  /** Singular noun for the count; pluralised with "s" unless `pluralNoun` is given. */
  noun?: string;
  pluralNoun?: string;
  isLoading?: boolean;
  /** The primary action, top right — "New document", "Add quote". */
  action?: ReactNode;
  /** Filter controls. Laid out in a row under the header. */
  filters?: ReactNode;
  children: ReactNode;
}

export function CollectionPage({
  title,
  count,
  noun = "item",
  pluralNoun,
  isLoading = false,
  action,
  filters,
  children,
}: CollectionPageProps) {
  const plural = pluralNoun || `${noun}s`;
  const subtitle = isLoading
    ? "Loading…"
    : count === undefined
      ? undefined
      : `${count} ${count === 1 ? noun : plural}`;

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-5xl px-6 py-10 md:px-8">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight md:text-3xl">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          {action}
        </header>

        {filters && (
          <div className="mb-6 flex flex-wrap items-center gap-2">{filters}</div>
        )}

        {children}
      </div>
    </AppLayout>
  );
}

/** Narrows the list already on screen. Distinct from ⌘K, which navigates. */
export function FilterInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-9 w-56 pl-9"
      />
    </div>
  );
}

export function CollectionList({ children }: { children: ReactNode }) {
  return (
    <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
      {children}
    </ul>
  );
}

interface CollectionRowProps {
  href: string;
  icon: ReactNode;
  /** What identifies this item — its own words, not its parent's name. */
  title: ReactNode;
  /** Context: where it came from, who said it. */
  subtitle?: ReactNode;
  /** Right-aligned, quiet — usually a timestamp. */
  meta?: ReactNode;
  /** Revealed on hover, positioned over the right edge. */
  actions?: ReactNode;
}

export function CollectionRow({
  href,
  icon,
  title,
  subtitle,
  meta,
  actions,
}: CollectionRowProps) {
  return (
    <li className="group relative">
      <Link href={href}>
        <div
          className={`flex cursor-pointer items-start gap-3 px-4 py-3.5 transition-colors hover:bg-muted/40 ${
            actions ? "pr-12" : ""
          }`}
        >
          <span className="mt-0.5 shrink-0">{icon}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <p className="truncate font-medium">{title}</p>
              {meta && (
                <span className="shrink-0 text-xs text-muted-foreground">{meta}</span>
              )}
            </div>
            {subtitle && (
              <p className="mt-0.5 truncate text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>
      </Link>
      {actions && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {actions}
        </div>
      )}
    </li>
  );
}

export function CollectionLoading({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-border px-6 py-16 text-center">
      <div className="mx-auto mb-3 h-7 w-7 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export function CollectionEmpty({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-border px-6 py-14 text-center">
      {icon && <div className="mb-3 flex justify-center">{icon}</div>}
      <p className="text-muted-foreground">{title}</p>
      {hint && (
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{hint}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
